import React, { useCallback } from 'react';
import { Navigate, Outlet, Route, RouteObject, Routes } from 'react-router-dom';
import authService from 'service/authService';
import * as ActionHistoryPage from '../domain/account/action-history';
import * as ContentProviderPage from '../domain/account/content-provider';
import * as LoginPage from '../domain/account/login';
import * as ProfilePage from '../domain/account/profile';
import * as UserPage from '../domain/account/user';
import * as ContentPage from '../domain/content';
import * as ClipTrailerPage from '../domain/content/clip-trailer';
import * as GenrePage from '../domain/content/genre';
import * as LibraryPage from '../domain/content/library';
import * as PeoplePage from '../domain/content/people';
import * as SeasonPage from '../domain/content/season';
import * as SeriesPage from '../domain/content/series';
import * as PermissionPage from '../domain/permission';
import * as RevenuePage from '../domain/revenue';
import * as UploadPage from '../domain/upload';
import * as VideoPage from '../domain/video';
import * as VideoSourcePage from '../domain/video/asset-source';

type PermissionRoute = RouteObject & { menuAction: string; menu: string };

const makeRoute = (
  path: string,
  element: React.ReactNode,
  menu: string,
  menuAction: string
): PermissionRoute => {
  return {
    path,
    element,
    menu,
    menuAction,
  };
};

const AppRoutes: React.FC = () => {
  const ProtectedRoute: React.FC = () => {
    const [isAuthenticated, setIsAuthenticated] = React.useState<boolean | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);

    React.useEffect(() => {
      authService
        .isAuthenticated()
        .then(isAuth => {
          setIsAuthenticated(isAuth);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, []);

    if (isLoading) {
      return null; // 또는 로딩 컴포넌트
    }

    return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
  };

  const contentRoutes: PermissionRoute[] = [
    makeRoute('/', <LibraryPage.LibraryPage />, 'content', 'read'),
    makeRoute('/main', <LibraryPage.LibraryPage />, 'content', 'read'),
    makeRoute('/library', <LibraryPage.LibraryPage />, 'content', 'read'),
    makeRoute('/playlists', <ContentPage.PlaylistPage />, 'content', 'read'),
    makeRoute('/playlists/edit/:id', <ContentPage.PlaylistEditPage />, 'content', 'edit'),
    makeRoute('/playlists/add', <ContentPage.PlaylistAddPage />, 'content', 'add'),
    makeRoute('/video/detail/:id', <LibraryPage.LibraryEditPage />, 'content', 'edit'),
    makeRoute('/video/detail/:id', <LibraryPage.LibraryEditPage />, 'content', 'edit'),
    makeRoute('/clip-trailer/detail/:id', <ClipTrailerPage.ClipTrailerDetail />, 'content', 'edit'),
  ];

  const uploadRoutes: PermissionRoute[] = [
    makeRoute('/import-upload', <UploadPage.ImportUploadPage />, 'import-upload', 'read'),
    makeRoute('/upload/video', <UploadPage.UploadVideoPage />, 'upload', 'read'),
    makeRoute('/video-source', <UploadPage.VideoSourcePage />, 'video-source', 'read'),
    makeRoute('/video-source/add', <UploadPage.VideoSourceAddPage />, 'video-source', 'add'),
    makeRoute('/video-source/edit/:id', <UploadPage.VideoSourceEditPage />, 'video-source', 'edit'),
    makeRoute(
      '/video-source/content',
      <UploadPage.VideoSourceContentListPage />,
      'video-source',
      'read'
    ),
    makeRoute('/video-source/odk', <UploadPage.VideoSourceODKPage />, 'video-source', 'read'),
  ];

  const userRoutes: PermissionRoute[] = [
    makeRoute('/user', <UserPage.UserPage />, 'user', 'read'),
    makeRoute('/user/add', <UserPage.UserAddPage />, 'user', 'add'),
    makeRoute('/user/edit/:id', <UserPage.UserEditPage />, 'user', 'edit'),
    makeRoute('/account', <ProfilePage.ProfilePage />, 'account', 'read'),
    makeRoute('/action-history', <ActionHistoryPage.ActionHistoryPage />, 'action-history', 'read'),
    makeRoute(
      '/content-provider',
      <ContentProviderPage.ContentProviderPage />,
      'content-provider',
      'read'
    ),
    makeRoute(
      '/content-provider/add',
      <ContentProviderPage.ContentProviderAddPage />,
      'content-provider',
      'add'
    ),
    makeRoute(
      '/content-provider/edit/:id',
      <ContentProviderPage.ContentProviderEditPage />,
      'content-provider',
      'edit'
    ),
  ];

  const metadataStructureRoutes: PermissionRoute[] = [
    makeRoute('/series', <SeriesPage.SeriesPage />, 'series', 'read'),
    makeRoute('/series/edit/:id', <SeriesPage.SeriesEditPage />, 'series', 'edit'),
    makeRoute('/series/add', <SeriesPage.SeriesAddPage />, 'series', 'add'),
    makeRoute('/season', <SeasonPage.Season />, 'season', 'read'),
    makeRoute('/season/add', <SeasonPage.SeasonAdd />, 'season', 'add'),
    makeRoute('/season/edit/:id', <SeasonPage.SeasonEdit />, 'season', 'edit'),
    makeRoute('/people', <PeoplePage.People />, 'people', 'read'),
    makeRoute('/people/add', <PeoplePage.PeopleAdd />, 'people', 'add'),
    makeRoute('/people/edit/:id', <PeoplePage.PeopleEdit />, 'people', 'edit'),
    makeRoute('/contract', <ContentPage.ContractPage />, 'contract', 'read'),
    makeRoute('/contract/add', <ContentPage.ContractAddPage />, 'contract', 'add'),
    makeRoute('/contract/edit/:id', <ContentPage.ContractEditPage />, 'contract', 'edit'),
    makeRoute('/category', <ContentPage.CategoryPage />, 'category', 'read'),
    makeRoute('/tag', <ContentPage.TagPage />, 'tag', 'read'),
    makeRoute('/tag/add', <ContentPage.TagAddPage />, 'tag', 'add'),
    makeRoute('/tag/edit/:id', <ContentPage.TagEditPage />, 'tag', 'edit'),
    makeRoute('/genre', <GenrePage.GenrePage />, 'genre', 'read'),
    makeRoute('/genre/add', <GenrePage.GenreAdd />, 'genre', 'add'),
    makeRoute('/genre/edit/:id', <GenrePage.GenreEdit />, 'genre', 'edit'),
  ];

  const revenueRoutes: PermissionRoute[] = [
    makeRoute('/revenue-share', <RevenuePage.RevenueSharePage />, 'revenue-share', 'read'),
    makeRoute(
      '/revenue-dashboard',
      <RevenuePage.RevenueDashboardPage />,
      'revenue-dashboard',
      'read'
    ),
  ];

  const permissionRoutes: PermissionRoute[] = [
    makeRoute('/role', <PermissionPage.RolePage />, 'role', 'read'),
    makeRoute('/role/add', <PermissionPage.RoleAdd />, 'role', 'add'),
    makeRoute('/role/edit/:id', <PermissionPage.RoleEdit />, 'role', 'edit'),
  ];

  const videoRoutes: PermissionRoute[] = [
    makeRoute('/assets-source', <VideoSourcePage.VideoSourcePage />, 'assets-source', 'read'),
    makeRoute('/assets', <VideoPage.AssetsPage />, 'assets', 'read'),
    makeRoute('/assets/edit/:id', <VideoPage.AssetsEditPage />, 'assets', 'edit'),
    makeRoute('/assets/add', <VideoPage.AssetAddPage />, 'assets', 'add'),
  ];

  const renderRoutes = useCallback(
    (routes: RouteObject[]) =>
      routes.map((route, index) => <Route key={index} path={route.path} element={route.element} />),
    []
  );

  return (
    <Routes>
      <Route path="/login" element={<LoginPage.LoginPage />} />
      <Route element={<ProtectedRoute />}>
        {renderRoutes(contentRoutes)}
        {renderRoutes(uploadRoutes)}
        {renderRoutes(userRoutes)}
        {renderRoutes(metadataStructureRoutes)}
        {renderRoutes(revenueRoutes)}
        {renderRoutes(permissionRoutes)}
        {renderRoutes(videoRoutes)}
      </Route>
    </Routes>
  );
};

export default AppRoutes;
